import type { AspectRatio } from '@lumen5/luminary';

import type { StyleReferenceAsset } from '../types';

import { ApiService } from 'js/src/client';

const GENX_MEDIA_TAG = 'genx';
const STYLE_TAG_PREFIX = 'style:';

/**
 * The preset generated media was made with, from its `genx` and `style:<slug>` tags. A preset's
 * own library images carry the style tag too, but not `genx`.
 */
export function genxStylePresetIdFromTags(tags: string[]): string | null {
    if (!tags.includes(GENX_MEDIA_TAG)) {
        return null;
    }
    const styleTag = tags.find((tag) => tag.startsWith(STYLE_TAG_PREFIX));
    return styleTag ? styleTag.slice(STYLE_TAG_PREFIX.length) : null;
}

/**
 * The preset an existing asset was generated with, or null when it is not GenX media or the
 * lookup fails. Scene payloads don't carry asset tags, so this fetches the asset's details.
 */
export async function fetchGenxStylePresetIdForAsset(assetUuid: string): Promise<string | null> {
    return (await fetchGenxMediaDetails([assetUuid])).get(assetUuid)?.presetId ?? null;
}

/** What an existing asset was generated with: its preset, and the prompt when one was recorded. */
export interface GenxMediaDetails {
    presetId: string | null;
    prompt: string;
}

const UNKNOWN_MEDIA: GenxMediaDetails = { presetId: null, prompt: '' };

/**
 * The generation details of several assets in one request. An asset that is not GenX media, is
 * out of reach, or whose lookup fails has no preset and no prompt.
 */
export async function fetchGenxMediaDetails(
    assetUuids: string[],
): Promise<Map<string, GenxMediaDetails>> {
    const details = new Map(assetUuids.map((uuid) => [uuid, UNKNOWN_MEDIA]));
    if (assetUuids.length === 0) {
        return details;
    }
    try {
        const { data } = await ApiService.bulkMediaAssetsList({
            query: { uuids: assetUuids.join(',') },
        });
        for (const container of data ?? []) {
            const media = container.background?.image ?? container.background?.video;
            if (media?.uuid) {
                details.set(media.uuid, {
                    presetId: genxStylePresetIdFromTags(media.tags ?? []),
                    prompt: container.generation_prompt ?? '',
                });
            }
        }
        return details;
    } catch {
        return details;
    }
}

export interface StylePreset {
    id: string;
    name: string;
    description: string;
    tags: string[];
    status?: string;
    thumbnailUrl?: string;
    previewVideoUrl?: string;
    preferredVideoModel?: string;
    supportedAspectRatios: AspectRatio[];
    styleReferenceAssets: StyleReferenceAsset[];
    imageStyleGuidelines: string;
    videoStyleGuidelines: string;
    videoNegativePrompt: string;
    templateId: string;
}

function mapPreset(preset: {
    slug: string;
    name: string;
    description: string;
    tags?: string[];
    status?: string;
    thumbnail_url?: string;
    previewVideoUrl?: string;
    preferredVideoModel?: string;
    supportedAspectRatios?: string[];
    styleReferenceAssets: { uuid: string; displayUrl: string }[];
    imageStyleGuidelines: string;
    videoStyleGuidelines: string;
    videoNegativePrompt?: string;
    templateId: string;
}): StylePreset {
    return {
        id: preset.slug,
        name: preset.name,
        description: preset.description,
        tags: preset.tags ?? [],
        status: preset.status,
        thumbnailUrl: preset.thumbnail_url ?? '',
        previewVideoUrl: preset.previewVideoUrl ?? '',
        preferredVideoModel: preset.preferredVideoModel,
        supportedAspectRatios: (preset.supportedAspectRatios ?? []) as AspectRatio[],
        styleReferenceAssets: preset.styleReferenceAssets.map((asset) => ({
            uuid: asset.uuid,
            displayUrl: asset.displayUrl,
        })),
        imageStyleGuidelines: preset.imageStyleGuidelines,
        videoStyleGuidelines: preset.videoStyleGuidelines,
        videoNegativePrompt: preset.videoNegativePrompt ?? '',
        templateId: preset.templateId,
    };
}

export async function fetchStylePresets(options?: {
    includeDrafts?: boolean;
    showAll?: boolean;
    restricted?: boolean;
}): Promise<StylePreset[]> {
    const query: Record<string, string> = {};
    if (options?.includeDrafts) {
        query.include_drafts = 'true';
    }
    if (options?.showAll) {
        query.all = 'true';
    }
    if (options?.restricted === false) {
        query.restricted = 'false';
    }
    const { data } = await ApiService.genxStylePresetsList({ query });
    if (!data) {
        throw new Error('Failed to fetch style presets');
    }

    return data.map(mapPreset);
}

export async function fetchAllStylePresets(): Promise<StylePreset[]> {
    const { data } = await ApiService.genxStylePresetsList({ query: { all: 'true' } });
    if (!data) {
        throw new Error('Failed to fetch style presets');
    }

    return data.map(mapPreset);
}
